import React, { useEffect, useState, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { MdVerified, MdContentCopy } from "react-icons/md";
import { BASE_API_URL, BASE_DOMAIN } from "../helpers/Config";
import axios from "axios";
import toast from "react-hot-toast";

const ProfileScreen = ({ theme = "light" }) => {
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [UserProfile, setUserProfile] = useState({
        name: "",
        user_id: "",
        balance: 0,
        usdt: 0,
        code: "",
    });

    // ---------- PWA install states ----------
    const [isRunningInPWA, setIsRunningInPWA] = useState(false);
    const [hasNativePrompt, setHasNativePrompt] = useState(false);

    const navigate = useNavigate();

    const profileOptions = [
        { id: "edit", label: "Edit Profile", icon: "👤", description: "Update your personal information", color: "primary", path: "edit-profile" },
        { id: "coin-transaction", label: "All Transaction History", icon: "🪙", description: "View all your transactions of coin", color: "info", path: "transaction-history" },
        { id: "usdt-transaction", label: "USDT Transaction History", icon: "💵", description: "View all your transactions of USDT", color: "info", path: "usdt-transaction-history" },
        { id: "deposit", label: "Deposit History", icon: "⬇️", description: "View all your deposit history of USDT", color: "info", path: "deposit-history" },
        { id: "withdrawl", label: "Withdrawl History", icon: "⬆️", description: "View all your withdrawl history of USDT", color: "info", path: "withdrawl-history" },
        { id: "coupon", label: "Coupons", icon: "🎫", description: "View & claim coupon codes", color: "info", path: "coupon" },
        { id: "claim-commission", label: "Claim Commission", icon: "💰", description: "Claim your earned commission", color: "warning", path: "commission-claim" },
        { id: "subordinate-data", label: "Subordinate Data", icon: "📈", description: "View subordinate data", color: "warning", path: "subordinate-data" },
        { id: "commission", label: "Commission Chart", icon: "📊", description: "View level wise commission chart", color: "primary", path: "commission-chart" },
        { id: "security", label: "Security", icon: "🔐", description: "Change your login password", color: "danger", path: "change-password" },
        { id: "team", label: "Trading Team", icon: "👥", description: "View my trading team", color: "secondary", path: "team" },
        { id: "support", label: "Help & Support", icon: "🎧", description: "24/7 customer support", color: "dark", path: "support" },

        // Install + Logout
        { id: "install-app", label: "Install App", icon: "📱", description: "Install app on your device", color: "success", path: null, isInstall: true },
        { id: "logout", label: "Logout", icon: "🚪", description: "Sign out from your account", color: "dark", path: null, isLogout: true },
    ];

    const isDark = theme === "dark";

    // ---------- standalone detection ----------
    const detectStandalone = useCallback(() => {
        const isStandaloneDisplayMode =
            window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
        const isIOSStandalone = window.navigator.standalone === true;
        return Boolean(isStandaloneDisplayMode || isIOSStandalone);
    }, []);



    // ---------- PWA lifecycle ----------
    useEffect(() => {
        const update = () => {
            const running = detectStandalone();
            setIsRunningInPWA(running);
            setHasNativePrompt(Boolean(window.__TB_PWA_PROMPT__));
        };

        update();

        // When global prompt becomes available
        const onAvailable = () => update();

        // When app installed
        const onInstalled = () => update();

        window.addEventListener("tb-pwa-prompt-available", onAvailable);
        window.addEventListener("tb-pwa-installed", onInstalled);

        // watch display-mode changes
        const mm = window.matchMedia ? window.matchMedia("(display-mode: standalone)") : null;
        const onMMChange = () => update();
        if (mm && mm.addEventListener) mm.addEventListener("change", onMMChange);
        else if (mm && mm.addListener) mm.addListener(onMMChange);

        return () => {
            window.removeEventListener("tb-pwa-prompt-available", onAvailable);
            window.removeEventListener("tb-pwa-installed", onInstalled);
            if (mm && mm.removeEventListener) mm.removeEventListener("change", onMMChange);
            else if (mm && mm.removeListener) mm.removeListener(onMMChange);
        };
    }, [detectStandalone]);

    // ---------- Install handler ----------
    const handleInstallClick = async () => {
        if (isRunningInPWA) return;

        const promptEvent = window.__TB_PWA_PROMPT__;
        if (!promptEvent) {
            toast.error("Install prompt not available. PWA is not installable yet.");
            return;
        }

        try {
            promptEvent.prompt();
            const choice = await promptEvent.userChoice;

            if (choice?.outcome === "accepted") {
                toast.success("Installation started!");
            } else {
                toast.error("Installation cancelled.");
                return;
            }
        } catch (e) {
            console.error(e);
            toast.error("Failed to open install prompt.");
        }
    };


    // ---------- Theme styles ----------
    const styles = useMemo(
        () => ({
            container: {
                backgroundColor: isDark ? "#111827" : "#ffffff",
                color: isDark ? "#f9fafb" : "#111827",
            },
            card: {
                backgroundColor: isDark ? "#020617" : "#ffffff",
                border: `1px solid ${isDark ? "#1f2937" : "#e5e7eb"}`,
            },
            text: {
                primary: isDark ? "#f9fafb" : "#111827",
                secondary: isDark ? "#9ca3af" : "#6b7280",
            },
            totalBalanceCard: {
                borderRadius: "8px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
                minHeight: "60px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                background: "linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)",
            },
            usdtBalanceCard: {
                borderRadius: "8px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
                minHeight: "60px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                background: "linear-gradient(135deg, #02c39a 0%, #84cc16 100%)",
            },
            referralCard: {
                borderRadius: "12px",
                background: isDark ? "linear-gradient(135deg, #4f46e5 0%, #7e22ce 100%)" : "linear-gradient(135deg, #ffd89b 0%, #19547b 100%)",
            },
            actionButton: {
                borderRadius: "8px",
                height: "55px",
                padding: "6px 4px",
                fontSize: "12px",
            },
            balanceAmount: {
                color: "#ffffff",
                fontWeight: "bold",
                textShadow: "0 1px 2px rgba(0,0,0,0.4)",
            },
            balanceLabel: {
                color: "#e5e7eb",
                fontWeight: 600,
                textShadow: "0 1px 2px rgba(0,0,0,0.4)",
                opacity: 0.96,
            },
            hoverBg: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.03)",
            modal: {
                backgroundColor: isDark ? "#020617" : "#ffffff",
                color: isDark ? "#f9fafb" : "#111827",
            },
            listBorder: isDark ? "#1f2937" : "#e5e7eb",
        }),
        [isDark]
    );

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.03, delayChildren: 0.1 } },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: "easeOut" } },
    };

    // ---------- Logout handlers ----------
    const handleLogout = () => setShowLogoutModal(true);
    const confirmLogout = () => {
        setShowLogoutModal(false);
        localStorage.removeItem("a5srf4wer7w1cd");
        navigate("/");
    };
    const cancelLogout = () => setShowLogoutModal(false);

    // ---------- Referral copy ----------
    const copyReferralLink = () => {
        const refer_link = `${BASE_DOMAIN}/register/${UserProfile?.code}`;
        navigator.clipboard
            .writeText(refer_link)
            .then(() => toast.success("Referral link copied to clipboard!"))
            .catch(() => {
                const textArea = document.createElement("textarea");
                textArea.value = refer_link;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand("copy");
                document.body.removeChild(textArea);
                toast.success("Referral link copied to clipboard!");
            });
    };

    // ---------- Fetch profile ----------
    const FetchProfile = async () => {
        try {
            const response = await axios.post(`${BASE_API_URL}/account/profile`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });

            const data = response?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error);
                }
                return;
            }

            if (data?.data) setUserProfile({ ...data.data });
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        FetchProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const textClass = isDark ? "text-light" : "text-dark";
    const textMutedClass = isDark ? "text-light-emphasis" : "text-muted";
    const cardClass = isDark ? "bg-dark text-light" : "bg-white text-dark";


    const filteredProfileOptions = useMemo(() => {
        return profileOptions.filter((opt) => {
            if (opt.id !== "install-app") return true;
            if (isRunningInPWA) return false;
            return hasNativePrompt;

        });
    }, [profileOptions, isRunningInPWA, hasNativePrompt]);


    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="h-100 overflow-auto p-2"
            style={styles.container}
        >
            {/* Avatar / Header */}
            <motion.div initial={{ y: -10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.3 }} className="text-center mb-3">
                <motion.div className="position-relative d-inline-block mb-2" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <div className="avatar-gradient position-relative">
                        <img
                            src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80"
                            alt="Profile"
                            className="rounded-circle object-fit-cover border border-3 border-white shadow-sm"
                            style={{ width: "70px", height: "70px" }}
                        />
                        <div className="bg-success rounded-circle position-absolute bottom-0 end-0 border border-2 border-white" style={{ width: "12px", height: "12px" }} />
                    </div>
                </motion.div>

                <div className="d-flex align-items-center justify-content-center mb-1">
                    <h1 className={`h4 fw-bold mb-0 me-1 ${textClass}`}>{UserProfile?.name}</h1>
                    <span className="text-primary fs-5" title="Verified">
                        <MdVerified />
                    </span>
                </div>
                <p className={`mb-1 small ${textMutedClass}`}>{UserProfile?.user_id}</p>
                <p className={`mb-2 small ${textMutedClass}`}>Professional Trader • VIP Member</p>

                {/* Balances */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="row g-2 mb-2">
                    <div className="col-6">
                        <motion.div className="card border-0 p-2 text-center" whileHover={{ scale: 1.02 }} transition={{ duration: 0.1 }} style={styles.totalBalanceCard}>
                            <div className="h6 fw-bold mb-1 text-center small" style={styles.balanceAmount}>
                                {UserProfile?.balance}
                            </div>
                            <small className="text-center small" style={styles.balanceLabel}>
                                Total Balance
                            </small>
                        </motion.div>
                    </div>

                    <div className="col-6">
                        <motion.div className="card border-0 p-2 text-center" whileHover={{ scale: 1.02 }} transition={{ duration: 0.1 }} style={styles.usdtBalanceCard}>
                            <div className="h6 fw-bold mb-1 text-center small" style={styles.balanceAmount}>
                                {UserProfile?.usdt}
                            </div>
                            <small className="text-center small" style={styles.balanceLabel}>
                                USDT Balance
                            </small>
                        </motion.div>
                    </div>
                </motion.div>
            </motion.div>

            {/* Referral card */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }} className="card border-0 shadow-sm mb-3" style={styles.referralCard}>
                <div className="card-body p-3">
                    <div className="d-flex align-items-center mb-2">
                        <span className="fs-5 me-2">👥</span>
                        <div>
                            <h6 className="fw-bold mb-0 small text-white">Refer & Earn</h6>
                            <small className="text-white-50">Invite friends and earn rewards</small>
                        </div>
                    </div>

                    <div className="d-flex align-items-center rounded p-2" style={{ backgroundColor: "rgba(255,255,255,0.2)" }}>
                        <input
                            type="text"
                            value={`${BASE_DOMAIN}/register/${UserProfile?.code}`}
                            readOnly
                            className="form-control border-0 bg-transparent small text-white"
                            style={{ fontSize: "12px" }}
                            onClick={(e) => e.target.select()}
                        />
                        <motion.button className="btn btn-light btn-sm ms-2 d-flex align-items-center" onClick={copyReferralLink} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} style={{ borderRadius: "6px", minWidth: "60px" }}>
                            <MdContentCopy className="me-1" size={14} />
                            <span className="small">Copy</span>
                        </motion.button>
                    </div>
                </div>
            </motion.div>

            {/* Deposit / Swap / Withdraw buttons */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="row g-2 mb-3">
                <div className="col-4">
                    <Link to="../fund-deposit" style={{ textDecoration: "none" }}>
                        <motion.button className="btn btn-success w-100 d-flex flex-column align-items-center justify-content-center" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} style={styles.actionButton}>
                            <span style={{ fontSize: "16px", marginBottom: "2px" }}>💰</span>
                            <span className="fw-bold" style={{ fontSize: "12px", lineHeight: "1.2" }}>Deposit</span>
                            <span className="text-white pb-1" style={{ fontSize: "9px", lineHeight: "1.1" }}>Deposit USDT</span>
                        </motion.button>
                    </Link>
                </div>

                <div className="col-4">
                    <Link to="../usdt-swap" style={{ textDecoration: "none" }}>
                        <motion.button className="btn btn-warning w-100 d-flex flex-column align-items-center justify-content-center" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} style={styles.actionButton}>
                            <span style={{ fontSize: "16px", marginBottom: "2px" }}>🔄</span>
                            <span className="fw-bold" style={{ fontSize: "12px", lineHeight: "1.2" }}>Swap</span>
                            <span className="text-black pb-1" style={{ fontSize: "9px", lineHeight: "1.1" }}>Coin ⇄ USDT</span>
                        </motion.button>
                    </Link>
                </div>

                <div className="col-4">
                    <Link to="../withdrawl" style={{ textDecoration: "none" }}>
                        <motion.button className="btn btn-primary w-100 d-flex flex-column align-items-center justify-content-center" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} style={styles.actionButton}>
                            <span style={{ fontSize: "16px", marginBottom: "2px" }}>💳</span>
                            <span className="fw-bold" style={{ fontSize: "12px", lineHeight: "1.2" }}>Withdraw</span>
                            <span className="text-white pb-1" style={{ fontSize: "9px", lineHeight: "1.1" }}>Withdraw USDT</span>
                        </motion.button>
                    </Link>
                </div>
            </motion.div>

            {/* Profile options */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} className={`card border-0 shadow-sm ${cardClass}`} style={styles.card}>
                <div className="card-header bg-transparent border-0 py-2">
                    <h5 className={`mb-0 fw-bold small ${textClass}`}>Account Management</h5>
                </div>

                <motion.div className="card-body p-0" variants={containerVariants} initial="hidden" animate="visible">
                    {filteredProfileOptions.map((option) => {
                        if (option.isLogout) {
                            return (
                                <motion.div
                                    key={option.id}
                                    variants={itemVariants}
                                    className="border-bottom"
                                    style={{ borderColor: styles.listBorder, cursor: "pointer" }}
                                    whileHover={{ backgroundColor: styles.hoverBg, transition: { duration: 0.1 } }}
                                    onClick={handleLogout}
                                >
                                    <div className="d-flex align-items-center justify-content-between p-2">
                                        <div className="d-flex align-items-center">
                                            <span className={`fs-6 me-2 p-2 rounded-circle bg-${option.color} bg-opacity-10 ${textClass}`} style={{ width: "38px", height: "38px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                {option.icon}
                                            </span>
                                            <div>
                                                <div className={`fw-semibold small ${textClass}`}>{option.label}</div>
                                                <small className={`small ${textMutedClass}`}>{option.description}</small>
                                            </div>
                                        </div>
                                        <span className={`fs-6 ${textMutedClass}`}>›</span>
                                    </div>
                                </motion.div>
                            );
                        }

                        if (option.isInstall) {
                            return (
                                <motion.div
                                    key={option.id}
                                    variants={itemVariants}
                                    className="border-bottom"
                                    style={{ borderColor: styles.listBorder, cursor: "pointer" }}
                                    whileHover={{ backgroundColor: styles.hoverBg, transition: { duration: 0.1 } }}
                                    onClick={handleInstallClick}
                                >
                                    <div className="d-flex align-items-center justify-content-between p-2">
                                        <div className="d-flex align-items-center">
                                            <span className={`fs-6 me-2 p-2 rounded-circle bg-${option.color} bg-opacity-10 ${textClass}`} style={{ width: "38px", height: "38px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                {option.icon}
                                            </span>
                                            <div>
                                                <div className={`fw-semibold small ${textClass}`}>{option.label}</div>
                                                <small className={`small ${textMutedClass}`}>{option.description}</small>
                                            </div>
                                        </div>
                                        <span className="fs-6 text-success">⬇️</span>
                                    </div>
                                </motion.div>
                            );
                        }

                        return (
                            <Link key={option.id} to={"../" + (option?.path || "")} style={{ textDecoration: "none" }}>
                                <motion.div variants={itemVariants} className="border-bottom" style={{ borderColor: styles.listBorder }} whileHover={{ backgroundColor: styles.hoverBg, transition: { duration: 0.1 } }}>
                                    <div className="d-flex align-items-center justify-content-between p-2">
                                        <div className="d-flex align-items-center">
                                            <span className={`fs-6 me-2 p-2 rounded-circle bg-${option.color} bg-opacity-10 ${textClass}`} style={{ width: "38px", height: "38px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                {option.icon}
                                            </span>
                                            <div>
                                                <div className={`fw-semibold small ${textClass}`}>{option.label}</div>
                                                <small className={`small ${textMutedClass}`}>{option.description}</small>
                                            </div>
                                        </div>
                                        <span className={`fs-6 ${textMutedClass}`}>›</span>
                                    </div>
                                </motion.div>
                            </Link>
                        );
                    })}
                </motion.div>
            </motion.div>

            {/* Logout modal */}
            {showLogoutModal && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }} className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} tabIndex="-1">
                    <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.2 }} className="modal-dialog modal-dialog-centered">
                        <div className={`modal-content ${isDark ? "bg-dark text-light" : "bg-white text-dark"}`} style={styles.modal}>
                            <div className="modal-header border-0" style={{ borderColor: styles.listBorder }}>
                                <h5 className="modal-title fw-bold">Confirm Logout</h5>
                            </div>
                            <div className="modal-body py-0">
                                <p className={textClass}>Are you sure you want to logout from your account?</p>
                            </div>
                            <div className="modal-footer border-0" style={{ borderColor: styles.listBorder }}>
                                <button type="button" className={`btn btn-secondary btn-sm ${isDark ? "btn-dark" : ""}`} onClick={cancelLogout}>
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-danger btn-sm" onClick={confirmLogout}>
                                    Logout
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </motion.div>
    );
};

export default ProfileScreen;
